/**
 * PDF Printing Utilities
 * Handles client-side printing of PDF files with fallback options
 */

/**
 * Print a PDF blob by opening it in a new window and triggering print
 * @param {Blob} pdfBlob - The PDF data as a blob
 * @param {string} filename - The filename for the PDF
 * @param {Object} options - Additional options
 * @returns {Promise<boolean>} - Success status
 */
export const printPdf = async (pdfBlob, filename, options = {}) => {
  const {
    timeout = 3000,
    showProgress = true
  } = options;

  let url = null;

  try {
    if (showProgress) {
      console.log('🖨️ Preparing PDF for printing...');
    }

    // Create blob URL
    url = window.URL.createObjectURL(pdfBlob);
    
    // Try to open in new window for printing
    const printWindow = window.open(url, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
    
    if (printWindow) {
      if (showProgress) {
        console.log('📄 PDF window opened, waiting for content to load...');
      }

      // Wait for PDF to load and then print
      return new Promise((resolve) => {
        let printed = false;
        
        // Method 1: Try onload event
        printWindow.onload = () => {
          if (!printed) {
            printed = true;
            setTimeout(() => {
              try {
                printWindow.print();
                if (showProgress) {
                  console.log('✅ Print dialog triggered successfully');
                }
                resolve(true);
              } catch (err) {
                console.warn('⚠️ Print failed, trying fallback:', err);
                resolve(false);
              }
            }, 1000);
          }
        };

        // Method 2: Fallback with timeout
        setTimeout(() => {
          if (!printed && printWindow && !printWindow.closed) {
            try {
              printWindow.print();
              printed = true;
              if (showProgress) {
                console.log('✅ Print dialog triggered via timeout fallback');
              }
              resolve(true);
            } catch (err) {
              console.warn('⚠️ Timeout fallback print failed:', err);
              resolve(false);
            }
          } else if (!printed) {
            resolve(false);
          }
        }, timeout);

        // Method 3: Check if window is ready
        const checkReady = () => {
          if (!printed && printWindow && !printWindow.closed) {
            try {
              // Try to access the document to see if it's loaded
              if (printWindow.document && printWindow.document.readyState === 'complete') {
                printWindow.print();
                printed = true;
                if (showProgress) {
                  console.log('✅ Print dialog triggered via readyState check');
                }
                resolve(true);
              } else {
                setTimeout(checkReady, 500);
              }
            } catch (err) {
              // Cross-origin or other issues, try timeout fallback
              setTimeout(() => {
                if (!printed && printWindow && !printWindow.closed) {
                  try {
                    printWindow.print();
                    printed = true;
                    if (showProgress) {
                      console.log('✅ Print dialog triggered via cross-origin fallback');
                    }
                    resolve(true);
                  } catch (finalErr) {
                    console.warn('⚠️ Final print attempt failed:', finalErr);
                    resolve(false);
                  }
                } else {
                  resolve(false);
                }
              }, 1000);
            }
          }
        };
        
        checkReady();
      });
    } else {
      // Popup blocked
      if (showProgress) {
        console.warn('⚠️ Popup blocked, cannot open PDF window');
      }
      return false;
    }
  } catch (err) {
    console.error('❌ Error in printPdf:', err);
    return false;
  } finally {
    // Clean up URL after a delay
    setTimeout(() => {
      try {
        if (url) {
          window.URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.warn('⚠️ Could not revoke URL:', err);
      }
    }, 10000);
  }
};

/**
 * Download a PDF blob as a file
 * @param {Blob} pdfBlob - The PDF data as a blob
 * @param {string} filename - The filename for the PDF
 */
export const downloadPdf = (pdfBlob, filename) => {
  try {
    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up URL
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
    
    console.log('📥 PDF downloaded successfully');
  } catch (err) {
    console.error('❌ Error downloading PDF:', err);
  }
};

/**
 * Print or download PDF with automatic fallback
 * @param {Blob} pdfBlob - The PDF data as a blob
 * @param {string} filename - The filename for the PDF
 * @param {Object} options - Additional options
 */
export const printOrDownloadPdf = async (pdfBlob, filename, options = {}) => {
  const { showProgress = true } = options;
  
  if (showProgress) {
    console.log('🔄 Attempting to print PDF...');
  }
  
  // Try to print first
  const printSuccess = await printPdf(pdfBlob, filename, { ...options, showProgress });
  
  if (printSuccess) {
    if (showProgress) {
      console.log('✅ PDF printed successfully');
    }
    return { success: true, method: 'print' };
  } else {
    // Fallback to download
    if (showProgress) {
      console.log('📥 Print failed, downloading PDF instead...');
    }
    
    downloadPdf(pdfBlob, filename);
    return { success: true, method: 'download' };
  }
};

/**
 * Check if the browser supports PDF printing
 * @returns {boolean} - Whether PDF printing is supported
 */
export const isPdfPrintingSupported = () => {
  // Check if window.open is available
  if (typeof window.open !== 'function') {
    return false;
  }
  
  // Check if print function is available
  if (typeof window.print !== 'function') {
    return false;
  }
  
  // Check if Blob and URL.createObjectURL are supported
  if (typeof Blob !== 'function' || typeof window.URL?.createObjectURL !== 'function') {
    return false;
  }
  
  return true;
};

const printUtils = {
  printPdf,
  downloadPdf,
  printOrDownloadPdf,
  isPdfPrintingSupported
};

export default printUtils;
